// Source of truth for the /docs section: page metadata + sidebar structure.
// Mirrors the pattern in data/blog-posts.ts — content lives in content/docs/**
// as MDX, this file drives routing, <title>/description, and the sidebar nav.

export interface DocsPage {
  /** URL path segment(s) joined with "/". Empty string = /docs itself. */
  slug: string;
  /** Full page title — rendered as the page's <h1> and <title>. */
  title: string;
  /** Shorter label for the sidebar link. Falls back to `title` when omitted. */
  navLabel?: string;
  /** Meta description (~150–160 chars). */
  description: string;
  /** Sidebar section this page is grouped under. */
  section: string;
}

export const docsPages: DocsPage[] = [
  {
    slug: "",
    title: "Instanodes Documentation",
    navLabel: "Overview",
    description: "What Instanodes is and how its documentation is organized: managed RPC endpoints, dedicated nodes, validators, rollups, and appchains across 50+ blockchains.",
    section: "Get Started",
  },
  {
    slug: "get-started",
    title: "Get Started",
    navLabel: "Quickstart",
    description: "Create an Instanodes account, send your first JSON-RPC request, and migrate an existing app from another RPC provider.",
    section: "Get Started",
  },
  {
    slug: "chains",
    title: "Chains & Networks",
    navLabel: "Chains & Networks",
    description: "Every blockchain network Instanodes supports, EVM and non-EVM, with node types available and links to per-chain reference pages.",
    section: "Reference",
  },
  {
    slug: "products",
    title: "Products",
    navLabel: "Products",
    description: "Instanodes' eight product lines, from managed RPC endpoints to fully custom appchain builds, and which problem each one solves.",
    section: "Reference",
  },
  {
    slug: "api-reference",
    title: "API Reference: Basics",
    navLabel: "Basics",
    description: "JSON-RPC and WebSocket fundamentals for Instanodes endpoints: request shape, subscriptions, and how API key authentication works.",
    section: "API Reference",
  },
  {
    slug: "api-reference/rate-limits",
    title: "API Reference: Rate Limits by Tier",
    navLabel: "Rate Limits",
    description: "Requests-per-day and requests-per-second ceilings for every Instanodes plan, and how they apply regardless of which RPC method you call.",
    section: "API Reference",
  },
  {
    slug: "api-reference/methods",
    title: "API Reference: Common Methods & Error Handling",
    navLabel: "Methods & Errors",
    description: "Frequently used JSON-RPC methods with a request/response example, plus the standard JSON-RPC error codes Instanodes returns.",
    section: "API Reference",
  },
  {
    slug: "api-reference/webhooks",
    title: "API Reference: Webhooks & Real-Time Alerts",
    navLabel: "Webhooks & Alerts",
    description: "Configure infrastructure-level alerts, node health, sync status, and traffic anomalies, from the Instanodes dashboard.",
    section: "API Reference",
  },
  {
    slug: "guides/deploy-your-first-node",
    title: "Guide: Deploy Your First Node",
    navLabel: "Deploy your first node",
    description: "Step-by-step: choose a chain and node type, pick a region, deploy, and send a test request against your new Instanodes RPC endpoint.",
    section: "Guides",
  },
  {
    slug: "guides/migrate-your-rpc-endpoint",
    title: "Guide: Migrate an Existing dApp's RPC Endpoint",
    navLabel: "Migrate an RPC endpoint",
    description: "Move a live application from Alchemy, Infura, QuickNode, or a self-hosted node to Instanodes without rewriting request code.",
    section: "Guides",
  },
  {
    slug: "guides/set-up-a-validator-node",
    title: "Guide: Set Up a Validator Node",
    navLabel: "Set up a validator node",
    description: "Deploy and configure a proof-of-stake validator on Instanodes, including slashing protection and 24/7 alerting.",
    section: "Guides",
  },
  {
    slug: "guides/launch-a-rollup",
    title: "Guide: Launch a Rollup",
    navLabel: "Launch a rollup",
    description: "Choose a rollup stack, scope the sequencer and DA layer, and take a rollup from testnet to a managed mainnet launch.",
    section: "Guides",
  },
  {
    slug: "tooling",
    title: "Tooling",
    navLabel: "Tooling",
    description: "The Instanodes dashboard, scriptable/CLI-driven deployment options, and SDK and library compatibility for EVM and non-EVM chains.",
    section: "Tooling",
  },
  {
    slug: "billing",
    title: "Billing & Plans",
    navLabel: "Billing & Plans",
    description: "Request and throughput ceilings for every Instanodes tier, annual billing discounts, and what each plan is designed for.",
    section: "Billing",
  },
  {
    slug: "security",
    title: "Security & Compliance",
    navLabel: "Security & Compliance",
    description: "SOC 2 Type II certification, role-based access control, network-level DDoS protection, and enterprise security requirements.",
    section: "Security & Reliability",
  },
  {
    slug: "reliability",
    title: "Reliability & SLA",
    navLabel: "Reliability & SLA",
    description: "Multi-region failover, the contractual 99.95% uptime SLA, the public status page, and how Instanodes handles incidents.",
    section: "Security & Reliability",
  },
  {
    slug: "support",
    title: "Support & Community",
    navLabel: "Support & Community",
    description: "Support tiers by plan, and where to reach Instanodes on X, LinkedIn, GitHub, Discord, or directly for account-specific questions.",
    section: "Help",
  },
  {
    slug: "faq",
    title: "Frequently Asked Questions",
    navLabel: "FAQ",
    description: "Answers to the most common questions about deploying nodes, pricing, validator hosting, security, and migrating to Instanodes.",
    section: "Help",
  },
  {
    slug: "glossary",
    title: "Glossary",
    navLabel: "Glossary",
    description: "Plain-language definitions for the blockchain infrastructure terms used throughout the Instanodes documentation.",
    section: "Help",
  },
];

export function getDocsPage(slug: string): DocsPage | undefined {
  return docsPages.find((p) => p.slug === slug);
}

/** Fixed section order for the sidebar — matches the order sections first appear in docsPages. */
export interface DocsNavSection {
  title: string;
  items: { label: string; href: string; slug: string }[];
}

export function getDocsNav(): DocsNavSection[] {
  const sections: DocsNavSection[] = [];
  const bySection = new Map<string, DocsNavSection>();

  for (const page of docsPages) {
    let section = bySection.get(page.section);
    if (!section) {
      section = { title: page.section, items: [] };
      bySection.set(page.section, section);
      sections.push(section);
    }
    section.items.push({
      label: page.navLabel ?? page.title,
      href: page.slug === "" ? "/docs" : `/docs/${page.slug}`,
      slug: page.slug,
    });
  }

  return sections;
}
